// 登录页 —— 深色壳 + 电蓝品牌 · mock / 真 API
import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/components/Toast';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { BrandLogo } from '@/components/BrandLogo';
import { Mail, Lock, ArrowRight } from 'lucide-react';
import { login, USE_MOCK } from '@/api/auth';
import { useLocalePath } from '@/i18n/useLocalePath';
import { mapApiError } from '@/lib/apiErrorMessages';

export default function LoginPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { localePath: lp } = useLocalePath();
  const [mode, setMode] = useState<'signin' | 'signup'>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const goAfterLogin = () => {
    const from = (location.state as { from?: string } | null)?.from;
    navigate(from || lp('/cardholder/dashboard'), { replace: true });
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) {
      toast.error(t('auth.fillRequired'));
      return;
    }
    setSubmitting(true);
    try {
      if (mode === 'signup') {
        toast.error('请通过商户邀请链接注册（/register?invite=…）');
        return;
      }
      const res = await login(email.trim(), password);
      if (!res.ok) {
        toast.error(t(mapApiError(res.code, res.error)));
        return;
      }
      toast.success(USE_MOCK ? t('auth.welcomeBackMock') : t('auth.welcomeBack'));
      const me = (await import('@/api/client')).loadSession()?.me;
      if (me && me.emailVerified === false) {
        navigate(lp('/verify-email'), { replace: true });
        return;
      }
      goAfterLogin();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center px-5 relative overflow-hidden">
      <div className="absolute inset-0 bg-hero-mesh pointer-events-none" />
      <div className="absolute -top-24 -right-24 w-80 h-80 bg-brand-600/25 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -left-20 w-72 h-72 bg-brand-400/15 rounded-full blur-3xl pointer-events-none" />

      <div className="relative w-full max-w-md animate-fade-up">
        <div className="flex justify-end mb-4">
          <LanguageSwitcher variant="full" />
        </div>

        <div className="text-center mb-8">
          <BrandLogo to={lp('/')} variant="dark" size="lg" className="justify-center" />
        </div>

        <div className="rounded-3xl border border-white/10 bg-white/[0.04] backdrop-blur-xl p-6 sm:p-8 shadow-elevated">
          <div className="flex gap-1 p-1 bg-white/5 rounded-xl mb-6">
            <button
              type="button"
              onClick={() => setMode('signin')}
              className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all min-h-[44px] cursor-pointer ${
                mode === 'signin'
                  ? 'bg-brand-gradient text-white shadow-sm'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              {t('auth.signIn')}
            </button>
            <button
              type="button"
              onClick={() => setMode('signup')}
              className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all min-h-[44px] cursor-pointer ${
                mode === 'signup'
                  ? 'bg-brand-gradient text-white shadow-sm'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              {t('auth.createAccount')}
            </button>
          </div>

          <form onSubmit={(e) => void submit(e)} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-slate-400 mb-1.5">
                {t('auth.email')}
              </label>
              <div className="relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <input
                  type="text"
                  inputMode="email"
                  autoComplete="username"
                  spellCheck={false}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-3 py-3 text-white text-sm placeholder:text-slate-600 focus:outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 transition"
                  placeholder="you@example.com"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-400 mb-1.5">
                {t('auth.password')}
              </label>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <input
                  type="password"
                  autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-3 py-3 text-white text-sm placeholder:text-slate-600 focus:outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 transition"
                  placeholder="••••••••"
                />
              </div>
            </div>
            <button type="submit" className="btn-primary w-full mt-2" disabled={submitting}>
              {submitting ? t('common.submitting') : mode === 'signin' ? t('auth.signIn') : t('auth.createAccount')}
              {!submitting && <ArrowRight className="w-4 h-4" />}
            </button>
          </form>

          <p className="mt-6 text-center text-xs text-slate-500">
            {mode === 'signin' ? (
              <>
                {t('auth.noAccount')}{' '}
                <button
                  type="button"
                  className="text-brand-400 hover:underline font-medium cursor-pointer"
                  onClick={() => setMode('signup')}
                >
                  {t('auth.createAccount')}
                </button>
              </>
            ) : (
              <>
                {t('auth.hasAccount')}{' '}
                <button
                  type="button"
                  className="text-brand-400 hover:underline font-medium cursor-pointer"
                  onClick={() => setMode('signin')}
                >
                  {t('auth.signIn')}
                </button>
              </>
            )}
          </p>
        </div>

        <p className="text-center text-[11px] text-slate-600 mt-6">
          <Link to={lp('/')} className="hover:text-slate-400 transition-colors">
            ← Allinks
          </Link>
        </p>
      </div>
    </div>
  );
}
